<template>
  <transition name="modal">
    <div
      :class="
        [
          'mdr-modal-mask',
          `mdr-modal-mask__${positionAlpha}`,
          `mdr-modal-mask__${sizeAlpha}`
        ].concat(size === ModalSize.lg ? ['mdr-modal-fullscreen'] : [])
      "
    >
      <div
        @click.self="$emit('close')"
        :class="
          ['mdr-modal-wrapper', `mdr-modal-wrapper__${positionAlpha}`].concat(
            size === ModalSize.lg ? ['mdr-modal-fullscreen'] : []
          )
        "
      >
        <div
          :class="[
            'mdr-modal-content',
            `mdr-modal-content__${positionAlpha}`,
            `mdr-modal-content__${sizeAlpha}`
          ]"
        >
          <button
            v-if="closeButtonVisible"
            @click="$emit('close')"
            :class="{ 'mdr-modal-close-button--rtl': isLangRtl($i18n.locale) }"
            class="mdr-modal-close-button"
            title="Close"
            type="button"
            value="Close"
          >
            <svg
              fill="none"
              height="16"
              viewBox="0 0 16 16"
              width="16"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M15 1L1 15" stroke="black" stroke-width="2" />
              <path d="M1 1L15 15" stroke="black" stroke-width="2" />
            </svg>
          </button>
          <div v-if="$slots.header" class="mdr-modal-header">
            <slot name="header" />
          </div>
          <div v-if="$slots.body" class="mdr-modal-body">
            <slot name="body" />
          </div>
          <div v-if="$slots.footer" class="mdr-modal-footer">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
import { isLangRtl } from "@moderrotech/servers-communication-library/src/helpers/lang";
import _ from "lodash";
import { computed, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";

export enum ModalPosition {
  center,
  mobileBottom
}

export enum ModalSize {
  sm,
  md,
  lg
}

export default {
  props: {
    closeButtonVisible: {
      type: Boolean,
      required: false,
      default: true
    },
    position: ModalPosition,
    size: ModalSize
  },
  setup(props: object) {
    const store = useStore();
    const config = computed(() => store.state.config.config);

    onMounted(() => document.body.classList.add("mdr-modal-open"));

    onUnmounted(() => document.body.classList.remove("mdr-modal-open"));

    return {
      config,
      isLangRtl,
      ModalPosition,
      ModalSize,
      positionAlpha: computed(() =>
        _.kebabCase(
          _.find(
            _.entries(ModalPosition),
            ([key, val]) => val === _.get(props, "position")
          )?.[0]
        )
      ),
      sizeAlpha: computed(() =>
        _.kebabCase(
          _.find(
            _.entries(ModalSize),
            ([key, val]) => val === _.get(props, "size", ModalSize.md)
          )?.[0]
        )
      ),
      ...props
    };
  }
};
</script>

<style lang="scss">
@import "../assets/styles/breakpoints.module";

.mdr-modal-open {
  overflow: hidden;
}
.mdr-modal-backdrop {
  display: block;
  background: rgba(0, 0, 0, 0.7);
  position: fixed;

  width: 100vw;
  height: 100vh;
  overflow: hidden;
  left: 0;
  top: 0;
}
.mdr-modal-mask {
  display: table;
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: rgba(24, 48, 109, 0.55);
  transition: opacity 0.3s ease;
  overflow-x: hidden;
  overflow-y: auto;

  &__lg {
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    display: flex;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
  }

  & + .modal-mask {
    display: none;
  }

  &__mobile-bottom {
    display: none;

    @media #{$iphone-xs} {
      display: table;
    }
  }
}

.mdr-modal-wrapper {
  display: table-cell;
  height: 100%;
  width: 100%;

  &__center {
    vertical-align: middle;
  }

  &__mobile-bottom {
    vertical-align: bottom;
  }
}

.mdr-modal-content {
  margin: 0 auto;
  background-color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  transition: all 0.3s ease;
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;

  &__center {
    border-radius: 23px;
    width: calc(100% - 22px);
    max-width: 370px;
    min-height: 346px;
    padding: 32px;
  }

  &__mobile-bottom {
    border-radius: 16px 16px 0 0;
    width: 100%;
    padding: 51px 27px 26px 27px;
  }

  &__lg {
    max-width: none;
    //padding: 0 30px;
    padding: 0;
    width: 100%;
    height: 100%;
    border-radius: 0;

    .mdr-modal-header {
      display: flex;
      flex-shrink: 0;
      align-items: center;
      justify-content: space-between;
      padding: 1rem;
      border-bottom: 1px solid #dee2e6;
    }

    .mdr-modal-body {
      overflow-y: auto;
      position: relative;
      flex: 1 1 auto;
      //padding: 1rem;
      padding: 0;
    }

    @media #{$ipad-pro} {
      //padding-left: 32px;
      //padding-right: 32px;
      padding-left: 0;
      padding-right: 0;
    }
  }

  &__md {
    max-width: 500px;
  }

  &__sm {
    max-width: 370px;
  }
}

.mdr-modal-body {
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  flex-direction: column;
  align-items: center;
  padding: 0;
}

.mdr-modal-footer {
  width: 100%;
}

.mdr-modal-default-button {
  display: block;
  margin-top: 1rem;
}

/*
   * The following styles are auto-applied to elements with
   * transition="modal" when their visibility is toggled
   * by Vue.js.
   *
   * You can easily play with the modal transition by editing
   * these styles.
   */

.mdr-modal-enter {
  opacity: 0;
}

.mdr-modal-leave-active {
  opacity: 0;
}

.mdr-modal-enter .mdr-modal-container,
.mdr-modal-leave-active .mdr-modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}

.mdr-modal-close-button {
  position: absolute;
  top: 21px;
  right: 22px;

  display: flex;
  flex-wrap: wrap;
  padding: 0;

  background: none;
  border: none;
  outline: none;
  cursor: pointer;

  &--rtl {
    right: auto;
    left: 22px;
  }

  &:hover,
  &:active {
    opacity: 0.9;
  }
}

.mdr-modal-fullscreen {
  display: block;

  .mdr-modal-wrapper {
    display: block;

    .mdr-modal-content {
      width: 100%;
      border-radius: 0;
    }
  }
}
</style>
